import React, { Suspense, useMemo } from 'react'
import { csx } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'

import { ProductPromotions } from '../../../core/components'
import { useProductPageUrlParams } from '../hooks'
import type { ProductSkuPromotions_Product$key } from './__generated__/ProductSkuPromotions_Product.graphql'

const productSkuFragment = graphql`
  fragment ProductSkuPromotions_Product on Product {
    skus {
      edges {
        node {
          id
          promotions(addresses: $addresses) {
            edges {
              node {
                id
                name
                highlight
              }
            }
          }
        }
      }
    }
  }
`

type ProductSkuPromotionsContentProps = {
  productRef: ProductSkuPromotions_Product$key
}

const ProductSkuPromotionsContent = ({
  productRef,
}: ProductSkuPromotionsContentProps) => {
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productSkuFragment, productRef)

  const selectedSku = useMemo(
    () => product.skus.edges.find((sku) => sku.node.id === selectedSkuId),
    [product.skus.edges, selectedSkuId]
  )

  // TODO: create util inline fragment
  const promotions = useMemo(() => {
    return selectedSku?.node.promotions?.edges.map((promotionEdge) => ({
      id: promotionEdge.node.id,
      name: promotionEdge.node.name,
      highlight: !!promotionEdge.node.highlight,
    }))
  }, [selectedSku?.node.promotions?.edges])

  if (!promotions?.length) {
    return null
  }

  return (
    <div
      className={csx({ marginBottom: '$space-5' })}
      data-testid="product-promotions-badge"
    >
      <ProductPromotions promotions={promotions} popoverDirection="down" />
    </div>
  )
}

export type ProductSkuPromotionsProps = {
  productRef: ProductSkuPromotions_Product$key
}

export const ProductSkuPromotions = (props: ProductSkuPromotionsProps) => (
  <Suspense fallback={null}>
    <ProductSkuPromotionsContent {...props} />
  </Suspense>
)
